import React, { useState, useEffect } from 'react';
import { getCountryFlag, getFlagIconUrl, getCountryName, getValidCountryCode } from '../utils/countryFlags';
import { getCachedFlagSvg, isFlagCached } from '../utils/flagCache';
import { flagPerformanceMonitor } from '../utils/flagPerformanceMonitor';

interface FlagIconProps {
  countryCode: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * A component to display a country flag, with fallback to SVG images if emoji flags don't render properly
 * Uses caching mechanism to avoid redundant loading of the same flags
 */
const FlagIcon: React.FC<FlagIconProps> = ({ 
  countryCode, 
  size = 'md',
  className = '' 
}) => {
  const [useEmoji, setUseEmoji] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [flagSrc, setFlagSrc] = useState<string | undefined>(undefined);
  
  // Get emoji flag
  const flagEmoji = getCountryFlag(countryCode);
  // Get country name for accessibility
  const countryName = getCountryName(countryCode);
  
  // Size classes
  const sizeClasses = {
    sm: 'text-lg w-5 h-5',
    md: 'text-xl w-6 h-6',
    lg: 'text-2xl w-8 h-8'
  };

  // Load flag from cache or fetch new
  useEffect(() => {
    if (!useEmoji) {
      flagPerformanceMonitor.startLoad();
      
      const validCode = getValidCountryCode(countryCode);
      const cachedSrc = getCachedFlagSvg(
        validCode,
        () => {
          setLoaded(true);
          flagPerformanceMonitor.recordCacheHit();
        },
        () => {
          console.warn(`Failed to load flag image for ${countryCode}`);
          setUseEmoji(true);
          flagPerformanceMonitor.recordCacheMiss();
        }
      );
      
      // If we got a cached version immediately, use it
      if (cachedSrc) {
        setFlagSrc(cachedSrc);
        setLoaded(true);
        flagPerformanceMonitor.recordCacheHit();
      } else {
        flagPerformanceMonitor.recordCacheMiss();
      }
    }
  }, [countryCode, useEmoji]);

  // Handle load error for SVG images
  const handleImageError = () => {
    console.warn(`Failed to load flag image for ${countryCode}`);
    setUseEmoji(true);
  };

  // Handle image load success
  const handleImageLoad = () => {
    setLoaded(true);
  };

  // Try SVG if flag emoji might not be supported well
  useEffect(() => {
    // If the flag emoji looks suspicious (often just showing boxes or question marks),
    // use the SVG fallback instead
    if (flagEmoji.includes('🇦') || flagEmoji === '🌍') {
      // Check if we already have this flag cached
      const validCode = getValidCountryCode(countryCode);
      if (isFlagCached(validCode)) {
        setUseEmoji(false);
      }
    }
  }, [flagEmoji, countryCode]);

  return (
    <span 
      className={`inline-block ${sizeClasses[size]} ${className}`}
      title={countryName}
    >
      {useEmoji ? (
        <span role="img" aria-label={`Flag of ${countryName}`}>
          {flagEmoji}
        </span>
      ) : (
        <img 
          src={flagSrc || getFlagIconUrl(countryCode)}
          alt={`Flag of ${countryName}`}
          className={`w-full h-full object-contain ${loaded ? 'opacity-100' : 'opacity-0'}`}
          onError={handleImageError}
          onLoad={handleImageLoad}
        />
      )}
    </span>
  );
};

export default FlagIcon;
